// ============================================
// stripeService.js - Stripe Payment Service
// ============================================
// API service for Stripe checkout, subscriptions, and billing

import { apiRequest } from './api';

// ============================================
// STRIPE API ENDPOINTS
// ============================================

export const stripeService = {
  /**
   * Create a Stripe Checkout session
   * POST /api/stripe/create-checkout-session/
   * @param {string} plan - Plan ID: 'basic' | 'pilot_lifetime'
   * @returns {Promise<{data?: {checkout_url: string, session_id: string}, error?: string}>}
   */
  createCheckoutSession: async (plan) => {
    return apiRequest('/stripe/create-checkout-session/', {
      method: 'POST',
      body: JSON.stringify({ plan }),
    });
  },

  /**
   * Create a Stripe Checkout session with price_id directly
   * POST /api/stripe/create-checkout-session/
   * @param {string} priceId - Stripe price ID
   * @returns {Promise<{data?: {checkout_url: string, session_id: string}, error?: string}>}
   */
  createCheckoutWithPriceId: async (priceId) => {
    return apiRequest('/stripe/create-checkout-session/', {
      method: 'POST',
      body: JSON.stringify({ price_id: priceId }),
    });
  },

  /**
   * Create a Stripe Customer Portal session for managing subscriptions
   * POST /api/stripe/create-portal-session/
   * @returns {Promise<{data?: {portal_url: string}, error?: string}>}
   */
  createPortalSession: async () => {
    return apiRequest('/stripe/create-portal-session/', {
      method: 'POST',
    });
  },

  /**
   * Get available subscription plans
   * GET /api/stripe/plans/
   * @returns {Promise<{data?: {plans: Array}, error?: string}>}
   */
  getPlans: async () => {
    return apiRequest('/stripe/plans/');
  },

  /**
   * Get current user's subscription status
   * GET /api/stripe/status/
   * @returns {Promise<{data?: {tier: string, subscription_active: boolean, has_subscription: boolean, is_lifetime: boolean}, error?: string}>}
   */
  getSubscriptionStatus: async () => {
    return apiRequest('/stripe/status/');
  },

  // ============================================
  // HELPER METHODS
  // ============================================

  /**
   * Redirect to Stripe Checkout
   * @param {string} plan - Plan ID: 'basic' | 'pilot_lifetime'
   */
  redirectToCheckout: async (plan) => {
    const result = await stripeService.createCheckoutSession(plan);
    
    if (result.error) {
      throw new Error(result.error);
    }

    if (result.data?.checkout_url) {
      window.location.href = result.data.checkout_url;
    } else {
      throw new Error('No checkout URL returned');
    }
  },

  /**
   * Redirect to Stripe Customer Portal
   */
  redirectToPortal: async () => {
    const result = await stripeService.createPortalSession();
    
    if (result.error) {
      throw new Error(result.error);
    }

    if (result.data?.portal_url) {
      window.location.href = result.data.portal_url;
    } else {
      throw new Error('No portal URL returned');
    }
  },

  /**
   * Check if user has an active paid subscription
   * @returns {Promise<boolean>}
   */
  hasActiveSubscription: async () => {
    const result = await stripeService.getSubscriptionStatus();
    
    if (result.error) {
      return false;
    }

    return result.data?.subscription_active || result.data?.is_lifetime || false;
  },

  /**
   * Get user's current tier
   * @returns {Promise<string>} 'free' | 'basic' | 'pilot_lifetime'
   */
  getCurrentTier: async () => {
    const result = await stripeService.getSubscriptionStatus();
    
    if (result.error) {
      return 'free';
    }

    return result.data?.tier || 'free';
  },
};

// ============================================
// PLAN DEFINITIONS (for frontend display)
// ============================================

export const PLANS = {
  free: {
    id: 'free',
    name: 'Free',
    price: 0,
    priceDisplay: '$0',
    interval: 'forever',
    features: [
      '3 messages per day',
      'Basic chat history',
      'Standard response speed',
    ],
    cta: 'Current Plan',
    canUpgrade: false,
  },
  basic: {
    id: 'basic',
    name: 'GhostInk Basic',
    price: 5,
    priceDisplay: '$5',
    interval: 'month',
    features: [
      'Unlimited messages during pilot',
      'Full chat history',
      'All writing modes',
      'Priority support',
    ],
    cta: 'Subscribe',
    canUpgrade: true,
    stripePlan: 'basic',
  },
  pilot_lifetime: {
    id: 'pilot_lifetime',
    name: 'Pilot Lifetime',
    price: 35.99,
    priceDisplay: '$35.99',
    interval: 'one-time',
    features: [
      'Lifetime unlimited access',
      'All writing modes forever',
      'Early supporter badge',
      'All future features included',
      'Priority support forever',
    ],
    cta: 'Get Lifetime Access',
    canUpgrade: true,
    stripePlan: 'pilot_lifetime',
    badge: 'Best Value',
    popular: true,
  },
};

/**
 * Get plan details by ID
 * @param {string} planId 
 * @returns {object} Plan details
 */
export function getPlanById(planId) {
  return PLANS[planId] || PLANS.free;
}

/**
 * Get all available plans as array
 * @returns {Array} Array of plan objects
 */
export function getAllPlans() {
  return Object.values(PLANS);
}

/**
 * Get upgradeable plans (excludes free)
 * @returns {Array} Array of paid plan objects
 */
export function getUpgradeablePlans() {
  return Object.values(PLANS).filter(plan => plan.canUpgrade);
}

export default stripeService;
